/**
 * Activities Step
 * 
 * Sport and activity preferences selection.
 * Issue #563 - First-Run Onboarding Flow
 */

import { useState } from 'react';

interface ActivitiesStepProps {
  onNext: () => void;
  onBack: () => void;
}

const SPORTS = [
  { id: 'running', label: 'Running' },
  { id: 'cycling', label: 'Cycling' },
  { id: 'hiking', label: 'Hiking' },
  { id: 'swimming', label: 'Swimming' },
  { id: 'walking', label: 'Walking' },
  { id: 'yoga', label: 'Yoga' },
];

export default function ActivitiesStep({ onNext, onBack }: ActivitiesStepProps) {
  const [primarySport, setPrimarySport] = useState<string>('');
  const [secondarySports, setSecondarySports] = useState<string[]>([]);

  const toggleSecondarySport = (sportId: string) => {
    setSecondarySports((prev) =>
      prev.includes(sportId)
        ? prev.filter((id) => id !== sportId)
        : [...prev, sportId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Call API to update sports preferences
    onNext();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-[--oro-text-primary]">
          What moves you?
        </h2>
        <p className="text-[--oro-text-secondary]">
          Select your primary activity and any others you enjoy.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Primary Sport */}
        <div>
          <label className="block text-sm font-medium text-[--oro-text-primary] mb-3">
            Primary Activity
          </label>
          <div className="grid grid-cols-2 gap-3">
            {SPORTS.map((sport) => (
              <button
                key={sport.id}
                type="button"
                onClick={() => setPrimarySport(sport.id)}
                className={`px-4 py-3 rounded-lg border font-medium transition-all ${
                  primarySport === sport.id
                    ? 'bg-[--oro-accent-primary] text-[--oro-text-on-accent] border-[--oro-accent-primary]'
                    : 'bg-[--oro-surface-secondary] text-[--oro-text-primary] border-[--oro-border-primary] hover:border-[--oro-accent-primary]'
                }`}
              >
                {sport.label}
              </button>
            ))}
          </div>
        </div>

        {/* Secondary Sports */}
        <div>
          <label className="block text-sm font-medium text-[--oro-text-primary] mb-3">
            Other Activities (Optional)
          </label>
          <div className="grid grid-cols-2 gap-3">
            {SPORTS.filter((s) => s.id !== primarySport).map((sport) => (
              <button
                key={sport.id}
                type="button"
                onClick={() => toggleSecondarySport(sport.id)}
                className={`px-4 py-3 rounded-lg border font-medium transition-all ${
                  secondarySports.includes(sport.id)
                    ? 'bg-[--oro-accent-primary] text-[--oro-text-on-accent] border-[--oro-accent-primary]'
                    : 'bg-[--oro-surface-secondary] text-[--oro-text-primary] border-[--oro-border-primary] hover:border-[--oro-accent-primary]'
                }`}
              >
                {sport.label}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2.5 rounded-lg bg-[--oro-surface-secondary] text-[--oro-text-primary] font-medium hover:bg-[--oro-surface-tertiary] transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!primarySport}
            className="px-6 py-2.5 rounded-lg bg-[--oro-accent-primary] text-[--oro-text-on-accent] font-medium hover:bg-[--oro-accent-hover] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
